import type { JobForYou } from '../types/jobs';

export type JobDatePreset = 'all' | '7d' | '30d' | '90d' | 'custom';

/**
 * Parse a job-related date field to epoch ms. Handles ISO strings, epoch seconds,
 * Firestore-like { seconds } objects, and YYYY-MM-DD prefixes.
 */
export function parseLooseDateMs(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'object' && value !== null && 'seconds' in (value as object)) {
    const s = (value as { seconds?: number }).seconds;
    return typeof s === 'number' ? s * 1000 : null;
  }
  const str = String(value).trim();
  if (!str) return null;
  if (/^\d{10,13}$/.test(str)) {
    const n = parseInt(str, 10);
    return n < 1e12 ? n * 1000 : n;
  }
  const ms = Date.parse(str);
  if (!Number.isNaN(ms)) return ms;
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    return Date.UTC(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  }
  return null;
}

/**
 * Resolve a unified date for a job:
 *   1. Job posting date — used when available
 *   2. Saved/updated in app — fallback (filteredJobUpdatedAt → filteredJobSavedAt → updatedAt → createdAt)
 *
 * Because every job in the user's list has a filteredJobSavedAt timestamp, this
 * always returns a value — no job is ever fully undated.
 */
export function getJobDateMs(job: JobForYou): number | null {
  const postingMs = parseLooseDateMs(job.postingDate);
  if (postingMs != null) return postingMs;

  // Fallback: app-side timestamps (per-resume filtered-jobs row, then catalog)
  return (
    parseLooseDateMs(job.filteredJobUpdatedAt) ??
    parseLooseDateMs(job.filteredJobSavedAt) ??
    parseLooseDateMs(job.updatedAt ?? job.createdAt)
  );
}

export function getDateRangeMs(
  preset: JobDatePreset,
  customFrom: string,
  customTo: string,
): { fromMs: number | null; toMs: number | null } {
  const now = Date.now();
  if (preset === 'all') {
    return { fromMs: null, toMs: null };
  }
  if (preset === '7d') {
    return { fromMs: now - 7 * 24 * 60 * 60 * 1000, toMs: now };
  }
  if (preset === '30d') {
    return { fromMs: now - 30 * 24 * 60 * 60 * 1000, toMs: now };
  }
  if (preset === '90d') {
    return { fromMs: now - 90 * 24 * 60 * 60 * 1000, toMs: now };
  }
  let fromMs: number | null = null;
  let toMs: number | null = null;
  if (customFrom) {
    const t = new Date(`${customFrom}T00:00:00`).getTime();
    if (!Number.isNaN(t)) fromMs = t;
  }
  if (customTo) {
    const t = new Date(`${customTo}T23:59:59.999`).getTime();
    if (!Number.isNaN(t)) toMs = t;
  }
  return { fromMs, toMs };
}

export function jobPassesDateFilter(
  job: JobForYou,
  preset: JobDatePreset,
  customFrom: string,
  customTo: string,
): boolean {
  const { fromMs, toMs } = getDateRangeMs(preset, customFrom, customTo);
  if (fromMs == null && toMs == null) return true;

  const ms = getJobDateMs(job);
  if (ms == null) return true; // should never happen — always has a fallback date

  if (fromMs != null && ms < fromMs) return false;
  if (toMs != null && ms > toMs) return false;
  return true;
}
